import { AnalyticsApiResponse } from 'app/dashboard/analytics-models';
import { ChartMetrics } from 'app/shared/chart-legends/chart-legends';
import { EmbeddedPlaceholder, Widget } from './widgets.model';

// Mirrors the backend TargetDevice enum. Only surfaces that differ from the default (Website) are enumerated.
// A null/undefined targetDevice on a campaign means Website.
export enum TargetDevice {
  Custom = 'Custom',
  NativeApp = 'NativeApp',
}

export interface OnsiteCampaign {
  id?: number;
  siteId?: number;
  accountId?: number;
  name?: string;
  templateId?: number;
  type?: OnsiteCampaignType;
  widgets?: Array<Widget>;
  tags?: Array<CampaignTag>;
  statistics?: CampaignStatistics;
  status?: CampaignStatus;
  scheduling?: CampaignScheduling;
  urlSettings?: CampaignUrlSetting[];
  triggers?: CampaignTrigger[];
  lastPublished?: Date;
  lastUnpublished?: Date;
  createdTime?: Date;
  updatedTime?: Date;
  contentChangedAt?: string;
  placeholder?: EmbeddedPlaceholder;
  placeholderId?: number;
  hasInputFields?: boolean;
  classification?: OnsiteCampaignClassification;
  pausedReason?: OnsiteCampaignPausedReason;
  targetDevice?: TargetDevice;
  // AB Test Hypothesis
  description?: string;
  history?: AbTestHistory;
  testMetric?: AbTestMetric;
  audienceId?: string;
  // Only for front-end
  purposeTag?: CampaignTag;
  daysLeft?: number;
  isScheduled?: boolean;
  chart?: AnalyticsApiResponse;
  hasUnpublishedChanges?: boolean;
  referringCampaign?: OnsiteCampaign;
  friendCampaign?: OnsiteCampaign;
  referralCampaignGroup?: ReferralCampaignGroup;
  isDeleteDisabled?: boolean;
  isVirtual?: boolean;
  isGenerating?: boolean;
  showTags?: boolean;
  /**
   * @deprecated This method is moved to Widget
   */
  attributionType?: AttributionType;
}

export interface FilterAllOnsiteCampaign extends OnsiteCampaign {
  previewImageUrl: string;
  campaignStatistics: FilterAllCampaignsStatistics;
}

export interface FilterAllCampaignsResponse {
  campaigns: Array<FilterAllOnsiteCampaign>;
  count: number;
}

export interface FilterCampaignsResponse {
  campaigns: Array<OnsiteCampaign>;
  count: number;
}

export interface CampaignCategoryCountsResponse {
  total: number;
  categories: Record<string, number>;
}

export interface FilterAllCampaignsStatistics {
  campaignId: number;
  lastUpdated: Date;
  clickThrough: number;
  conversions: number;
  conversionPartial: number;
  couponsCopied: number;
  dismissal: number;
  open: number;
  share: number;
  surveyResponses: number;
  surveyResponsesPartial: number;
  formResponses: number;
  // Null when the campaign has no views.
  responseRate: number | null;
}

export interface ReferralCampaignGroup {
  id?: number;
  siteId?: number;
  referringCampaignId?: number;
  referringCampaign?: OnsiteCampaign;
  friendCampaignId?: number;
  friendCampaign?: OnsiteCampaign;
}

export interface ReferralCampaignGroupWithAnalytics extends ReferralCampaignGroup {
  chart?: AnalyticsApiResponse;
  legends?: {
    performanceChartRevenue?: ChartMetrics[];
    performanceChartReferralConversion?: ChartMetrics[];
    performanceChartShare?: ChartMetrics[];
    performanceChartReferralView?: ChartMetrics[];
  };
}

export enum AddOptions {
  Component = 'Component',
  Graphic = 'Graphic',
}

export enum AttributionType {
  Impression = 'Impression',
  Conversion = 'Conversion',
  Clickthrough = 'Clickthrough',
  CopyCoupon = 'CopyCoupon',
  Share = 'Share',
  None = 'None',
}

export interface AbTestHistory {
  description?: string;
  testMetric?: AbTestMetric;
  startDate?: string;
  endDate?: string;
  loosingWidget?: AbTestHistoryWidget;
  winningWidget?: AbTestHistoryWidget;
}

export interface AbTestHistoryWidget {
  id?: number;
  clicks?: number;
  conversions?: number;
  screenshot?: string;
  views?: number;
}

export enum AbTestMetric {
  Click = 'Click',
  Conversion = 'Conversion',
}

export enum OnsiteCampaignPausedReason {
  SurveyResponsesOverLimit = 'SurveyResponsesOverLimit',
  CouponsUsageAtPauseLimit = 'CouponsUsageAtPauseLimit',
}

export interface CampaignUrlSetting {
  id: number;
  path: string;
  isPathTypeInclude: boolean; // False is for exclude
}

export interface CampaignTrigger {
  id: number;
  type: CampaignTriggerType;
  value: string;
  settings?: any;
}

export interface CampaignRepetitionSetting {
  id?: number;
  type: CampaignRepetitionType;
  value?: number;
  option: CampaignRepetitionOption;
  shouldPersistState?: boolean;
}

export enum CampaignRepetitionType {
  Dismissal = 'Dismissal',
  Conversion = 'Conversion',
  ClickThrough = 'ClickThrough',
  Minimize = 'Minimize',
}

export enum CampaignRepetitionOption {
  ContinueShowing = 'ContinueShowing',
  DisableShowingForXMinutes = 'DisableShowingForXMinutes',
  DontShowAgain = 'DontShowAgain',
  DisableShowingForXPageViews = 'DisableShowingForXPageViews',
}

export enum CampaignRepetitionState {
  RememberState = 'rememberState',
  ShowPrimary = 'showPrimary',
}

export enum OnsiteCampaignType {
  Regular = 'Regular',
  Template = 'Template',
  ABTesting = 'ABTesting',
  Survey = 'Survey',
  Referral = 'Referral',
}

export enum CampaignTriggerType {
  Instantly = 'Instantly',
  AfterXPageviews = 'AfterXPageviews',
  AfterXSeconds = 'AfterXSeconds',
  ExitIntent = 'ExitIntent',
  Scroll = 'Scroll',
  InactiveForXSeconds = 'InactiveForXSeconds',
  Manual = 'Manual',
}

export interface CampaignScheduling {
  startDate?: string;
  endDate?: string;
  customDays?: string[];
  customHours?: number[];
}

export interface CampaignTag {
  id: number;
  name: string;
  color?: string;
  type?: CampaignTagType;
  usageCount?: number;
  // Front-end
  selected?: boolean;
}

export class CampaignPublishValidation {
  dataIsLoaded: boolean;
  campaignIsPublishable: boolean;
  nameIsSet: boolean;
  widgetValidation: Array<CampaignPublishValidationWidget>;

  constructor() {
    this.dataIsLoaded = false;
    this.nameIsSet = false;
    this.campaignIsPublishable = false;
    this.widgetValidation = [];
  }
}

export class CampaignPublishValidationWidget {
  hasSubmitButtonProblem: boolean;
  hasFormSettingsProblem: boolean;
  widgetId: number;

  constructor(widgetId: number) {
    this.widgetId = widgetId;
    this.hasSubmitButtonProblem = false;
    this.hasFormSettingsProblem = false;
  }
}

export enum CampaignTagType {
  Regular = 'Regular',
  Layout = 'Layout',
  Purpose = 'Purpose',
  Content = 'Content',
}

export enum CampaignStatus {
  Draft = 'Draft',
  Published = 'Published',
  Unpublished = 'Unpublished',
  Finished = 'Finished',
  Scheduled = 'Scheduled',
}

export enum ReferralTypes {
  Referring = 'referring',
  Friends = 'friends',
}

export interface AbTestStatistics {
  campaignId?: number;
  views?: number;
  conversions?: number;
  conversionRate?: number;
  clickthroughs?: number;
  ctr?: number;
  testMetric?: AbTestMetric;
  controlWidget?: {
    widgetId?: number;
    views?: number;
    conversions?: number;
    conversionRate?: number;
    clickthroughs?: number;
    conversionsPercentage?: number;
    clickthroughsPercentage?: number;
    ctr?: number;
  };
  challengerWidget?: {
    widgetId?: number;
    views?: number;
    conversions?: number;
    conversionRate?: number;
    clickthroughs?: number;
    conversionsPercentage?: number;
    clickthroughsPercentage?: number;
    ctr?: number;
  };
}

export interface CampaignSettings {
  id: string;
  allowMultipleOverlays?: boolean;
}

export interface CampaignFilterRequest {
  name?: string;
  id?: number;
  count?: number;
  filterSettings: FilterSettings;
  hasUnsavedChanges?: boolean;
}

export interface FilterSettings {
  targetDevice?: TargetDevice;
  statusFilter?: StatusFilter;
  typeFilter?: TypeFilter;
  componentFilter?: ComponentFilter;
  lastPublished?: DateFilter;
  lastUpdated?: DateFilter;
  createdTime?: DateFilter;
  nameFilter?: NameFilter;
  accountFilter?: AccountFilter;
  statisticsFilter?: StatisticsFilter;
  skip?: number;
  take?: number;
  tags?: number[];
  objectiveFilter?: string[];
  tacticFilter?: string[];
}

export interface FilterSettingsWithSort extends FilterSettings {
  sortFilter?: SortFilter;
}

export interface SortFilter {
  sortType: SortFilterType;
  pinnedRecentCount?: number;
}

export interface CampaignTableSort {
  column: string;
  direction: 'asc' | 'desc';
  /** The window the statistics columns show, so the order follows the figures on screen. */
  statisticsFrom: string;
  statisticsTo: string;
}

export enum SortFilterType {
  CreatedAscending = 'CreatedAscending',
  CreatedDescending = 'CreatedDescending',
  TitleAscending = 'TitleAscending',
  TitleDescending = 'TitleDescending',
  UpdatedAscending = 'UpdatedAscending',
  UpdatedDescending = 'UpdatedDescending',
  PublishedAscending = 'PublishedAscending',
  PublishedDescending = 'PublishedDescending',
  ViewsAscending = 'ViewsAscending',
  ViewsDescending = 'ViewsDescending',
  FormResponsesAscending = 'FormResponsesAscending',
  FormResponsesDescending = 'FormResponsesDescending',
  ResponseRateAscending = 'ResponseRateAscending',
  ResponseRateDescending = 'ResponseRateDescending',
  ClickthroughsAscending = 'ClickthroughsAscending',
  ClickthroughsDescending = 'ClickthroughsDescending',
  RecentThenStatus = 'RecentThenStatus',
}

export interface StatusFilter {
  isDraft: boolean;
  isPublished: boolean;
  isUnPublished: boolean;
  isScheduledByDate: boolean;
  isScheduledByDaysAndHours: boolean;
  isScheduled: boolean;
  isFinished: boolean;
}

export interface TypeFilter {
  isRegular: boolean;
  isSurvey: boolean;
  isForm: boolean;
  isPromotion: boolean;
  isReferral: boolean;
  isAbTest: boolean;
  isGamification: boolean;
  isQuiz: boolean;
  isNudge: boolean;
  isProductTour: boolean;
}

export interface ContentFilter {
  hasCustomStyles?: boolean;
  hasEventScripts?: boolean;
}

export interface ComponentFilter extends ContentFilter {
  hasInputFields?: boolean;
  variationsComparisonType?: ComparisonOperatorType;
  variations: number;
  variationsTo: number;
  stepsComparisonType?: ComparisonOperatorType;
  steps: number;
  stepsTo: number;
  componentType?: ComponentType;
}

export enum ComponentType {
  spinTheWheel,
  deadline,
  memory,
  couponCode,
  uniqueCouponCode,
  sticker,
  basicPhoneNumber,
  checkboxList,
  buttonChoices,
  shareButton,
}

export interface DateFilter {
  from?: string;
  to?: string;
}

export interface NameFilter {
  substring?: string;
}

export interface AccountFilter {
  siteIds?: number[];
}

export interface StatisticsFilter {
  viewsComparisonType?: ComparisonOperatorType;
  views: number;
  viewsTo: number;
  responseRateComparisonType?: ComparisonOperatorType;
  responseRate: number;
  responseRateTo: number;
  formResponsesComparisonType?: ComparisonOperatorType;
  formResponses: number;
  formResponsesTo: number;
  clickthroughsComparisonType?: ComparisonOperatorType;
  clickthroughs: number;
  clickthroughsTo: number;
}

export enum ComparisonOperatorType {
  greaterThanOrEqual = 'greaterThanOrEqual',
  lessThanOrEqual = 'lessThanOrEqual',
  between = 'between',
}

export class CampaignStatistics {
  views: number;
  conversions: number;
  conversionRate: number;
  clickthroughs: number;
  ctr: number;
  couponsCopied: number;
  couponsCopiedRate: number;
  dismissals: number;
  surveyResponses: number;
  surveyRate: number;
  surveyResponsesPartial: number;
  conversionPartial: number;
  surveyTotal?: number;
  conversionTotal?: number;
  share?: number;
  shareRate?: number;

  constructor(
    views: number,
    conversions: number,
    clickthroughs: number,
    couponsCopied: number,
    dismissals: number,
    surveyResponses: number,
    surveyResponsesPartial: number,
    conversionPartial: number,
    share: number,
  ) {
    this.views = views;
    this.conversions = conversions;
    this.clickthroughs = clickthroughs;
    this.couponsCopied = couponsCopied;
    this.dismissals = dismissals;
    this.surveyResponses = surveyResponses;
    this.surveyResponsesPartial = surveyResponsesPartial;
    this.conversionPartial = conversionPartial;
    this.share = share;

    if (this.clickthroughs && this.views && this.views !== 0) {
      if (this.clickthroughs !== 0) {
        this.ctr = parseFloat(((this.clickthroughs / this.views) * 100).toFixed(2));
      } else {
        this.ctr = 0;
      }
    }

    if (this.couponsCopied && this.views && this.views !== 0) {
      if (this.couponsCopied !== 0) {
        this.couponsCopiedRate = parseFloat(((this.couponsCopied / this.views) * 100).toFixed(2));
      } else {
        this.couponsCopiedRate = 0;
      }
    }

    if (this.share && this.views && this.views !== 0) {
      if (this.share !== 0) {
        this.shareRate = parseFloat(((this.share / this.views) * 100).toFixed(2));
      } else {
        this.shareRate = 0;
      }
    }

    this.conversionTotal = this.conversions + this.conversionPartial;
    if (this.conversionTotal && this.views && this.views !== 0) {
      if (this.conversionTotal !== 0) {
        this.conversionRate = CampaignStatistics.roundRate((this.conversionTotal / this.views) * 100);
        this.couponsCopiedRate = parseFloat(((this.couponsCopied / this.conversionTotal) * 100).toFixed(2));
      } else {
        this.conversionRate = 0;
      }
    }

    this.surveyTotal = this.surveyResponses + this.surveyResponsesPartial;
    if (this.surveyTotal && this.views && this.views !== 0) {
      if (this.surveyTotal !== 0) {
        this.surveyRate = CampaignStatistics.roundRate((this.surveyTotal / this.views) * 100);
      } else {
        this.surveyRate = 0;
      }
    }
  }

  private static roundRate(rate: number): number {
    if (rate < 10) {
      return parseFloat(rate.toFixed(2));
    }
    if (rate < 100) {
      return parseFloat(rate.toFixed(1));
    }
    return parseFloat(rate.toFixed());
  }
}

export interface ResponseListProperties {
  newListName?: string;
  existingResponseListId?: string;
}

export enum ResponseListType {
  Nps = 'Nps',
  Csat = 'Csat',
}

export enum InputType {
  Text = 'Text',
  Email = 'Email',
  FullName = 'FullName',
  FirstName = 'FirstName',
  LastName = 'LastName',
  Phone = 'Phone',
  RadioButtons = 'RadioButtons',
  ButtonChoices = 'ButtonChoices',
  Checkboxes = 'Checkboxes',
  DropDown = 'DropDown',
  Date = 'Date',
  Consent = 'Consent',
  StarRating = 'StarRating',
  SmileyRating = 'SmileyRating',
  ThumbRating = 'ThumbRating',
  NumericalRating = 'NumericalRating',
  NpsRating = 'NpsRating',
  Csat5Rating = 'Csat5Rating',
  CountrySelect = 'CountrySelect',
  ElapsedTimeMs = 'ElapsedTimeMs',
}

export interface InputDefinition {
  name: string;
  key: string;
}

export interface ResponseListColumn {
  name: string;
  key: string;
  type: InputType;
  inputDefintions: Array<InputDefinition>;
}

export interface ResponseListsWithStatistics {
  id: string;
  columns: Array<ResponseListColumn>;
  groupId: number;
  name: string;
  createdDate: Date;
  lastUpdatedDate: Date;
  scoreColumn: string;
  commentColumn: string;
  itemsAdded: number;
  lastItemAddedDate: Date | null;
}

export interface OnsiteCampaignClassification {
  classifiedAt?: string;
  labels: OnsiteCampaignClassificationLabel[];
}

export interface OnsiteCampaignClassificationLabel {
  labelType: string;
  value: string;
}
